import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { client } from "../../utils/supabase";

// Modal para overridear manualmente la fecha de cobro estimada de una entry del Backlog.
// Soporta dos orígenes:
//   - "sales": columna directa sales.fecha_cobro_estimada (por id).
//   - "contratos": key dentro del objeto de cuota en el JSONB sales_contratos.meses[cuota_index].
// Cartelería NO se edita acá (entries sintéticas proyectadas — fuera de alcance).

const formatFechaLarga = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [yyyy, mm, dd] = f.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

const formatMonto = (entry) => {
  const n = Number(entry?.importe || 0);
  const num = n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  return entry?.moneda_dolar ? `US$ ${num}` : num;
};

const EditarFechaCobroModal = ({ show, onHide, entry, onSaved }) => {
  const [fecha, setFecha] = useState(entry?.fecha_override_actual || "");
  const [guardando, setGuardando] = useState(false);

  if (!entry) return null;

  const esContrato = entry.origen === "contratos";

  const persistir = async (nuevaFecha) => {
    if (entry.origen === "sales") {
      const { error } = await client.from("sales").update({ fecha_cobro_estimada: nuevaFecha }).eq("id", entry.sale_id);
      if (error) throw error;
      return;
    }
    if (entry.origen === "contratos") {
      const { data, error: errLeer } = await client
        .from("sales_contratos")
        .select("meses")
        .eq("id", entry.contrato_id)
        .single();
      if (errLeer) throw errLeer;
      const mesesActualizado = [...(data?.meses || [])];
      const cuota = { ...mesesActualizado[entry.cuota_index] };
      if (nuevaFecha) {
        cuota.fecha_cobro_estimada = nuevaFecha;
      } else {
        delete cuota.fecha_cobro_estimada;
      }
      mesesActualizado[entry.cuota_index] = cuota;
      const { error } = await client
        .from("sales_contratos")
        .update({ meses: mesesActualizado })
        .eq("id", entry.contrato_id);
      if (error) throw error;
      return;
    }
    throw new Error(`Origen no soportado: ${entry.origen}`);
  };

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("Seleccioná una fecha");
      return;
    }
    setGuardando(true);
    try {
      await persistir(fecha);
      toast.success("Fecha de cobro actualizada");
      onSaved?.();
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  const handleVolverAutomatica = async () => {
    setGuardando(true);
    try {
      await persistir(null);
      toast.success("Restaurada a fecha automática");
      onSaved?.();
    } catch (e) {
      toast.error(`Error al restaurar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Editar fecha de cobro estimada</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Cliente:</strong> {entry.cliente || "—"}
          </div>
          <div>
            <strong>Monto:</strong> {formatMonto(entry)}
          </div>
          {esContrato && (
            <div>
              <strong>Cuota:</strong> {entry.cuota_index + 1}
            </div>
          )}
          <div>
            <strong>Fecha automática:</strong> {formatFechaLarga(entry.fecha_automatica)}
            <span style={{ color: "#888", marginLeft: 6, fontSize: "0.8rem" }}>(sin override)</span>
          </div>
        </div>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Fecha de cobro override</Form.Label>
          <Form.Control type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} disabled={guardando} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <div>
          {entry.tiene_override && (
            <Button variant="outline-secondary" size="sm" onClick={handleVolverAutomatica} disabled={guardando}>
              Volver a automática
            </Button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
            Cerrar
          </Button>
          <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditarFechaCobroModal;
